import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

interface IOptions {
  lockBodyScroll?: boolean
  exitOnEscape?: boolean
}

const useFullscreenOverlay = (options: IOptions = {}) => {
  const { lockBodyScroll = true, exitOnEscape = true } = options

  const portalContainerRef = useRef<HTMLDivElement>(document.createElement('div'))
  const inlineHostRef = useRef<HTMLDivElement | null>(null)

  const [isFullscreen, setIsFullscreen] = useState(false)

  useLayoutEffect(() => {
    const container = portalContainerRef.current
    container.style.display = 'flex'
    container.style.flex = '1'
    container.style.minHeight = '0'
    container.style.minWidth = '0'
    container.style.top = '0'
    container.style.right = '0'
    container.style.bottom = '0'
    container.style.left = '0'
  }, [])

  useLayoutEffect(() => {
    const container = portalContainerRef.current
    const parent = isFullscreen ? document.body : inlineHostRef.current
    if (!parent) return

    container.style.position = isFullscreen ? 'fixed' : 'absolute'
    container.style.zIndex = isFullscreen ? '2000' : ''
    parent.appendChild(container)
  }, [isFullscreen])

  useEffect(() => {
    return () => {
      portalContainerRef.current.remove()
    }
  }, [])

  useLayoutEffect(() => {
    if (!lockBodyScroll || !isFullscreen) return

    const body = document.body
    const prevOverflow = body.style.overflow
    body.style.overflow = 'hidden'

    return () => {
      body.style.overflow = prevOverflow
    }
  }, [isFullscreen, lockBodyScroll])

  useEffect(() => {
    if (!exitOnEscape || !isFullscreen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [exitOnEscape, isFullscreen])

  const enter = useCallback(() => setIsFullscreen(true), [])
  const exit = useCallback(() => setIsFullscreen(false), [])
  const toggle = useCallback(() => setIsFullscreen((prev) => !prev), [])

  return {
    isFullscreen,
    enter,
    exit,
    toggle,
    inlineHostRef,
    portalContainer: portalContainerRef.current,
  } as const
}

export default useFullscreenOverlay
